import * as React from 'react';
import { Box, Container, Typography } from '@mui/material';
import StickyFooter from './components/StickyFooter';
import { companyName } from '../defaults/AppDefaults';
import backgroundImg from '../images/pexels-gromakova-92378617-9146943.jpg';

export default function PrivacyPolicy() {
  return (
    <div style={{ backgroundColor: '#0f1013' }}>
      {/* Hero Header */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${backgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          textAlign: 'center',
          py: 10,
          px: 2,
          mt: 6,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Last updated: 31 July 2026
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container
        maxWidth="md"
        sx={{
          py: 8,
          px: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ color: '#fff' }}>
          {/* Introduction */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            1. Introduction
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Welcome to Barbers Limited. We are committed to protecting your
            personal information and your right to privacy. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your
            information when you use our platform and services. Please read this
            privacy policy carefully. If you do not agree with the terms of this
            privacy policy, please do not access the platform.
          </Typography>

          {/* Information We Collect */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            2. Information We Collect
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We collect information that you provide directly to us, including:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              Personal identification information (name, email address, phone
              number)
            </li>
            <li>Account credentials and profile information</li>
            <li>Booking and appointment details</li>
            <li>Payment and billing information</li>
            <li>Communications and correspondence with us</li>
            <li>Usage data and preferences</li>
            <li>Location data when you search for barbers</li>
          </Box>

          {/* How We Use Your Information */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            3. How We Use Your Information
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We use the information we collect to:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>Provide, maintain, and improve our services</li>
            <li>Process bookings and manage appointments</li>
            <li>Send booking confirmations, reminders, and notifications</li>
            <li>Process payments and prevent fraud</li>
            <li>
              Communicate with you about services, updates, and promotional
              offers
            </li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Analyze usage patterns and optimize user experience</li>
            <li>Comply with legal obligations and enforce our terms</li>
          </Box>

          {/* Information Sharing and Disclosure */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            4. Information Sharing and Disclosure
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We may share your information in the following circumstances:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              <strong>With Barbers and Shop Owners:</strong> We share your
              booking information with the barber or shop you have chosen to
              facilitate your appointment
            </li>
            <li>
              <strong>With Service Providers:</strong> We share information with
              third-party service providers who perform services on our behalf,
              such as payment processing, email delivery, and data analytics
            </li>
            <li>
              <strong>For Legal Reasons:</strong> We may disclose information if
              required by law, legal process, or governmental request
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with any
              merger, sale of company assets, or acquisition
            </li>
            <li>
              <strong>With Your Consent:</strong> We may share information for
              any other purpose with your consent
            </li>
          </Box>

          {/* Data Security */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            5. Data Security
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We implement appropriate technical and organizational security
            measures to protect your personal information against unauthorized
            access, alteration, disclosure, or destruction. However, no method
            of transmission over the internet or electronic storage is 100%
            secure, and we cannot guarantee absolute security.
          </Typography>

          {/* Data Retention */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            6. Data Retention
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this Privacy Policy, unless a
            longer retention period is required or permitted by law. When we no
            longer need your information, we will securely delete or anonymize
            it.
          </Typography>

          {/* Your Rights */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            7. Your Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Depending on your location, you may have the following rights
            regarding your personal information:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>Access and receive a copy of your personal data</li>
            <li>Rectify inaccurate or incomplete personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Object to or restrict processing of your personal data</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </Box>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            To exercise these rights, please contact us using the information
            provided below.
          </Typography>

          {/* Cookies and Tracking Technologies */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            8. Cookies and Tracking Technologies
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We use cookies and similar tracking technologies to collect and
            track information about your use of our platform. Cookies are small
            data files stored on your device. You can instruct your browser to
            refuse all cookies or to indicate when a cookie is being sent.
            However, if you do not accept cookies, you may not be able to use
            some portions of our services.
          </Typography>

          {/* Third-Party Services */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            9. Third-Party Services
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Our platform may contain links to third-party websites or services
            that are not operated by us. We have no control over and assume no
            responsibility for the content, privacy policies, or practices of
            any third-party sites or services. We encourage you to review the
            privacy policies of any third-party services you visit.
          </Typography>

          {/* Children's Privacy */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            10. Children's Privacy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Our services are not intended for individuals under the age of 18.
            We do not knowingly collect personal information from children under
            18. If you become aware that a child has provided us with personal
            information, please contact us, and we will take steps to delete
            such information.
          </Typography>

          {/* International Data Transfers */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            11. International Data Transfers
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Your information may be transferred to and maintained on computers
            located outside of your country or jurisdiction where data
            protection laws may differ. By using our services, you consent to
            the transfer of your information to our facilities and to those
            third parties with whom we share it as described in this Privacy
            Policy.
          </Typography>

          {/* Changes to This Privacy Policy */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            12. Changes to This Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We may update this Privacy Policy from time to time to reflect
            changes in our practices or for other operational, legal, or
            regulatory reasons. We will notify you of any material changes by
            posting the new Privacy Policy on this page and updating the "Last
            updated" date. We encourage you to review this Privacy Policy
            periodically.
          </Typography>

          {/* Contact Us */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            13. Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            If you have any questions or concerns about this Privacy Policy or
            our data practices, please contact us at:
          </Typography>
          <Box
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Barbers Limited</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Email: accounts@barbers.limited
            </Typography>
          </Box>

          {/* GDPR Compliance */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            14. GDPR Compliance (For EU/UK Residents)
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            If you are a resident of the European Union or United Kingdom, you
            have certain additional rights under the General Data Protection
            Regulation (GDPR), including:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>The right to be informed about how your data is being used</li>
            <li>The right of access to your personal data</li>
            <li>The right to rectification of inaccurate data</li>
            <li>The right to erasure ("right to be forgotten")</li>
            <li>The right to restrict processing</li>
            <li>The right to data portability</li>
            <li>The right to object to processing</li>
            <li>Rights related to automated decision-making and profiling</li>
          </Box>
          <Typography
            variant="body1"
            sx={{ mb: 6, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            The legal bases for our processing of your personal data include
            your consent, performance of a contract, compliance with legal
            obligations, and our legitimate business interests. You have the
            right to lodge a complaint with a supervisory authority if you
            believe your data protection rights have been violated.
          </Typography>
        </Box>
      </Container>

      <StickyFooter {...companyName} />
    </div>
  );
}
